import {FormLabel} from "@/components/form/FormLabel.tsx";
import {FormSubtext} from "@/components/formSubtext/FormSubtext.tsx";
import {Input} from "@/components/input/Input.tsx";
import {Select} from "@/components/select/Select.tsx";
import {SelectOption} from "@/components/select/Select.types.ts";
import {Switch} from "@/components/switch/Switch.tsx";
import {MetricBaselineMethod} from "@/lib/types/metricsExplorer.ts";
import {
  BASELINE_METHODS,
  ExplorerAnalysisConfig,
  MAX_ANOMALY_SIGMA,
  MAX_MOVING_AVERAGE_WINDOW,
  MIN_ANOMALY_SIGMA,
  MIN_MOVING_AVERAGE_WINDOW,
} from "@/pages/metricsExplorer/explorerConfig.ts";

import styles from "./AnalysisControls.module.scss";

interface AnalysisControlsProps {
  analysis: ExplorerAnalysisConfig;
  showDowntime: boolean;
  /** False when the chart covers more than one check, where a band means nothing. */
  downtimeAvailable: boolean;
  onChange: (patch: Partial<ExplorerAnalysisConfig>) => void;
  onShowDowntimeChange: (showDowntime: boolean) => void;
}

const BASELINE_METHOD_LABELS: Record<MetricBaselineMethod, string> = {
  WINDOW: "Mean and deviation",
  WINDOW_ROBUST: "Median and IQR",
};

type ToggleKey = "trend" | "movingAverage" | "anomaly" | "baseline" | "seasonality";

const TOGGLES: {key: ToggleKey; label: string; description: string}[] = [
  {key: "trend", label: "Trend", description: "Fits a line and reports its slope per day."},
  {key: "movingAverage", label: "Moving average", description: "Smooths short-term noise."},
  {
    key: "anomaly",
    label: "Anomaly band",
    description: "Marks points sitting outside the expected range.",
  },
  {
    key: "baseline",
    label: "Baseline figures",
    description: "Mean, median and percentiles for the window.",
  },
  {
    key: "seasonality",
    label: "Seasonality",
    description: "Profiles the metric by hour of day and weekday.",
  },
];

const toOption = (value: string, label: string): SelectOption => ({value, label});

/**
 * Chooses which analyses the server runs.
 *
 * Every figure here is computed server-side by the same engine an alert rule will use, so
 * what the chart shows and what an alert would fire on cannot drift apart.
 */
export const AnalysisControls = ({
  analysis,
  showDowntime,
  downtimeAvailable,
  onChange,
  onShowDowntimeChange,
}: AnalysisControlsProps) => (
  <div className={styles.panel} role="group" aria-label="Analysis">
    <div className={styles.toggles}>
      {TOGGLES.map((toggle) => (
        <Switch
          key={toggle.key}
          id={`analysis-${toggle.key}`}
          label={toggle.label}
          description={toggle.description}
          labelPosition="right"
          checked={analysis[toggle.key]}
          onChange={() => onChange({[toggle.key]: !analysis[toggle.key]})}
        />
      ))}
      <Switch
        id="analysis-downtime"
        label="Show downtime"
        description={
          downtimeAvailable
            ? "Shades the periods this check was down."
            : "Available when the chart covers a single check."
        }
        labelPosition="right"
        checked={showDowntime && downtimeAvailable}
        disabled={!downtimeAvailable}
        onChange={() => onShowDowntimeChange(!showDowntime)}
      />
    </div>

    {(analysis.movingAverage || analysis.anomaly) && (
      <div className={styles.params}>
        {analysis.movingAverage && (
          <div>
            <FormLabel forId="analysis-moving-average-window">Window (points)</FormLabel>
            <Input
              id="analysis-moving-average-window"
              type="number"
              size="small"
              min={MIN_MOVING_AVERAGE_WINDOW}
              max={MAX_MOVING_AVERAGE_WINDOW}
              value={analysis.movingAverageWindow}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                onChange({movingAverageWindow: Number(event.currentTarget.value)})
              }
            />
          </div>
        )}

        {analysis.anomaly && (
          <>
            <div>
              <FormLabel forId="analysis-anomaly-sigma">Sensitivity (σ)</FormLabel>
              <Input
                id="analysis-anomaly-sigma"
                type="number"
                size="small"
                step={0.5}
                min={MIN_ANOMALY_SIGMA}
                max={MAX_ANOMALY_SIGMA}
                value={analysis.anomalySigma}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  onChange({anomalySigma: Number(event.currentTarget.value)})
                }
              />
            </div>
            <div>
              <FormLabel forId="analysis-baseline-method">Baseline</FormLabel>
              <Select
                inputId="analysis-baseline-method"
                size="small"
                options={BASELINE_METHODS.map((method) => toOption(method, BASELINE_METHOD_LABELS[method]))}
                value={toOption(analysis.baselineMethod, BASELINE_METHOD_LABELS[analysis.baselineMethod])}
                onChange={(option) =>
                  onChange({
                    baselineMethod: ((option as SelectOption | null)?.value ?? "WINDOW") as MetricBaselineMethod,
                  })
                }
              />
              <FormSubtext subText="The median is unmoved by the spikes it is meant to catch." />
            </div>
          </>
        )}
      </div>
    )}
  </div>
);
